import { Injectable } from "@angular/core";
import { HttpService } from "./../http-service";
import { Observable } from "rxjs/Rx";

/**
 * Provider para o modulo EcoEnel Empresas > Reciclagens
 *
 * Fluxo de lancamentos de reciclagem para Grandes Geradores e Recicladores
 * Endpoints: /ecoenel-empresas/lancamentos/*
 *
 * Perfis EcoEnel: GRANDE GERADOR, RECICLADOR
 * Perfis admin (gestao): ADMINISTRADOR, GERENTE, DIRETORIA
 */
@Injectable()
export class EcoEnelEmpresasProvider {
  private _url: string = "/ecoenel-empresas/lancamentos";

  constructor(public http: HttpService) {}

  // =====================================================
  // DADOS AUXILIARES (COMUM A TODOS OS PERFIS)
  // =====================================================

  /**
   * Lista status disponiveis para filtros
   */
  public getStatus(): Observable<any> {
    return this.http.get(this._url + "/status").map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista motivos de recusa disponiveis
   */
  public getMotivosRecusa(): Observable<any> {
    return this.http.get(this._url + "/motivos-recusa").map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista recursos (tipos de residuos) ativos
   */
  public getRecursos(): Observable<any> {
    return this.http.get(this._url + "/recursos").map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista recursos distintos de um evento
   */
  public getRecursosDoEvento(eventoId: number): Observable<any> {
    return this.http.get(this._url + "/recursos-evento/" + eventoId).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista vigencias de um recurso para evento+reciclador
   * Retorna array com id, valor, periodo, texto
   */
  public getVigenciasRecurso(eventoId: number, recicladorId: number, recursoId: number): Observable<any> {
    return this.http.get(this._url + "/vigencias/" + eventoId + "/" + recicladorId + "/" + recursoId).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista recicladores ativos, opcionalmente filtrados por evento
   */
  public getRecicladores(eventoId?: number): Observable<any> {
    var url = this._url + "/recicladores";
    if (eventoId) {
      url += "?evento_id=" + eventoId;
    }
    return this.http.get(url).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Busca contrato do gerador por parceiro_id
   */
  public getContratoGerador(parceiroId: number): Observable<any> {
    return this.http.get(this._url + "/contrato-gerador/" + parceiroId).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Busca contrato do gerador do usuario autenticado (server-side)
   */
  public getMeuContratoGerador(): Observable<any> {
    return this.http.get(this._url + "/meu-contrato-gerador").map((res) => {
      return res.json().data;
    });
  }

  /**
   * Busca contrato do reciclador via recicladors_contratos
   */
  public getContratoReciclador(recicladorId: number): Observable<any> {
    return this.http.get(this._url + "/contrato-reciclador/" + recicladorId).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista eventos disponiveis conforme perfil do usuario
   */
  public getEventosDisponiveis(): Observable<any> {
    return this.http.get(this._url + "/eventos").map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista contratos de um evento
   */
  public getContratos(eventoId: number): Observable<any> {
    return this.http.get(this._url + "/contratos/" + eventoId).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Obtem detalhes de um lancamento/reciclagem
   */
  public getLancamento(id: number): Observable<any> {
    return this.http.get(this._url + "/" + id).map((res) => {
      return res.json().data;
    });
  }

  // =====================================================
  // ENDPOINTS PARA ADMIN/GERENTE/DIRETORIA
  // =====================================================

  /**
   * Lista todos os lancamentos (para visualizacao geral)
   * @param statusId Filtrar por status (opcional)
   */
  public getTodosLancamentos(filtros?: any): Observable<any> {
    var url = this._url + "/todos";
    var params = this._buildFiltroParams(filtros);
    if (params) {
      url += "?" + params;
    }
    return this.http.get(url).map((res) => {
      return res.json().data;
    });
  }

  // =====================================================
  // ENDPOINTS PARA GRANDE GERADOR
  // =====================================================

  /**
   * Lista lancamentos do Grande Gerador logado
   * @param statusId Filtrar por status (opcional)
   */
  public getMeusLancamentos(filtros?: any): Observable<any> {
    var url = this._url + "/meus";
    var params = this._buildFiltroParams(filtros);
    if (params) {
      url += "?" + params;
    }
    return this.http.get(url).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Cria um novo lancamento de reciclagem
   */
  public criarLancamento(lancamento: any): Promise<any> {
    return new Promise((success, reject) => {
      this.http
        .post(this._url, null, JSON.stringify(lancamento))
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  /**
   * Reenvia um lancamento cancelado/expirado
   */
  public reenviarLancamento(id: number): Promise<any> {
    return new Promise((success, reject) => {
      this.http
        .post(this._url + "/" + id + "/reenviar", null, null)
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  // =====================================================
  // ENDPOINTS PARA RECICLADOR / OPERADOR RECICLAGEM
  // =====================================================

  /**
   * Lista todos os lancamentos do reciclador
   * @param statusId Filtrar por status (opcional)
   */
  public getLancamentosReciclador(filtros?: any): Observable<any> {
    var url = this._url + "/reciclador";
    var params = this._buildFiltroParams(filtros);
    if (params) {
      url += "?" + params;
    }
    return this.http.get(url).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Lista lancamentos pendentes para o reciclador
   * @param recicladorId Para Admin/Gerente/Diretoria filtrar por reciclador especifico
   */
  public getLancamentosPendentes(recicladorId?: number): Observable<any> {
    var url = this._url + "/pendentes";
    if (recicladorId) {
      url += "?reciclador_id=" + recicladorId;
    }
    return this.http.get(url).map((res) => {
      return res.json().data;
    });
  }

  /**
   * Aprova um lancamento (cria reciclagem real no sistema)
   */
  public aprovarLancamento(id: number): Promise<any> {
    return new Promise((success, reject) => {
      this.http
        .post(this._url + "/" + id + "/aprovar", null, null)
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  /**
   * Recusa um lancamento com motivo
   * @param id ID do lancamento
   * @param reason Motivo da recusa (texto do motivo selecionado)
   * @param comment Observacao adicional (opcional)
   */
  public recusarLancamento(
    id: number,
    reason: string,
    comment?: string
  ): Promise<any> {
    return new Promise((success, reject) => {
      const body = {
        reason: reason,
        comment: comment || null,
      };

      this.http
        .post(this._url + "/" + id + "/recusar", null, JSON.stringify(body))
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  // =====================================================
  // VALIDAÇÃO INDIVIDUAL DE RESÍDUOS
  // =====================================================

  /**
   * Aprova um residuo individual
   */
  public aprovarResiduo(residuoId: number): Promise<any> {
    return new Promise((success, reject) => {
      this.http
        .post(this._url + "/residuo/" + residuoId + "/aprovar", null, null)
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  /**
   * Recusa um residuo individual com motivo
   */
  public recusarResiduo(
    residuoId: number,
    reason: string,
    comment?: string
  ): Promise<any> {
    return new Promise((success, reject) => {
      var body = {
        reason: reason,
        comment: comment || null,
      };

      this.http
        .post(this._url + "/residuo/" + residuoId + "/recusar", null, JSON.stringify(body))
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  /**
   * Finaliza a validacao de um lancamento
   */
  public finalizarValidacao(lancamentoId: number): Promise<any> {
    return new Promise((success, reject) => {
      this.http
        .post(this._url + "/" + lancamentoId + "/finalizar-validacao", null, null)
        .map((res) => res.json())
        .toPromise()
        .then((data) => {
          success(data);
        })
        .catch((err) => {
          reject(this._parseError(err));
        });
    });
  }

  // =====================================================
  // HELPERS
  // =====================================================

  /**
   * Constroi query string a partir de objeto de filtros
   * Suporta status_id (single ou array), data_ini, data_fim
   */
  private _buildFiltroParams(filtros: any): string {
    if (!filtros) return "";

    // Compatibilidade: se filtros for um numero, trata como status_id direto
    if (typeof filtros === "number" || (typeof filtros === "string" && !isNaN(Number(filtros)))) {
      return "status_id=" + filtros;
    }

    var parts: string[] = [];

    if (filtros.status_id) {
      if (Array.isArray(filtros.status_id)) {
        parts.push("status_id=" + filtros.status_id.join(","));
      } else {
        parts.push("status_id=" + filtros.status_id);
      }
    }
    if (filtros.data_ini) {
      parts.push("data_ini=" + filtros.data_ini);
    }
    if (filtros.data_fim) {
      parts.push("data_fim=" + filtros.data_fim);
    }

    return parts.join("&");
  }

  private _parseError(err: any): any {
    try {
      const body = JSON.parse(err._body);
      return {
        status: err.status || 500,
        msg: body.error || body.message || "Erro desconhecido",
      };
    } catch (e) {
      return {
        status: err.status || 500,
        msg: "Erro de comunicacao com o servidor",
      };
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/ecoenel-empresas/ecoenel-empresas.provider.ts